import { useEffect, useMemo, useRef } from 'react'

import mixpanel from 'mixpanel-browser'

import { useSplitEnabled } from '@buffer-mono/features'

import { isRealProduction } from '~publish/env'
import { logError } from '~publish/legacy/utils/logError'
import { isMixpanelReady } from '~publish/legacy/web/hooks/useMixpanelSessionReplay'

/**
 * Starts a Mixpanel session recording while the Bulk Post Upload tool is
 * engaged (this hook is mounted only while the dialog is open).
 *
 * Finding these replays in Mixpanel: we deliberately do NOT register a
 * `recording_context` super property. Publish analytics flow through Segment,
 * and `mixpanelSegmentMiddleware` (see `useMixpanelSessionReplay`) forwards only
 * `$mp_replay_id` to Segment events — a registered Mixpanel property would never
 * reach Mixpanel as a filter. The `Dialog Viewed` event already fired here with
 * `dialogName: 'bulkPostUpload'` carries `$mp_replay_id` once recording is
 * active, so that event is the feature-specific replay path.
 *
 * Ownership: the global initializer (`useMixpanelSessionReplay`) samples 0.5% of
 * sessions, so a replay may already be running when this hook mounts.
 * `start_session_recording()` is a no-op in that case — but the matching
 * `stop_session_recording()` is global and would terminate that pre-existing
 * replay. So we only start (and, on cleanup, stop) when no replay is already
 * active, i.e. when this hook genuinely owns the recording.
 *
 * Gated on real production and the `core-bulk-upload-mixpanel-replays` split so
 * recording can be switched off without a deploy.
 */
export function useMixpanelBulkUploadRecording(): void {
  const ownsRecordingRef = useRef(false)
  const isProduction = useMemo(() => isRealProduction(), [])
  const { isEnabled: isMixpanelReplaysEnabled } = useSplitEnabled(
    'core-bulk-upload-mixpanel-replays',
  )

  useEffect(() => {
    if (!isProduction || !isMixpanelReplaysEnabled || !isMixpanelReady()) {
      return
    }

    try {
      // Don't claim ownership of a replay the global sample already started —
      // stopping it on cleanup would cut that session's recording short.
      const replayProperties = mixpanel.get_session_recording_properties()
      const replayAlreadyActive =
        '$mp_replay_id' in replayProperties &&
        Boolean(replayProperties.$mp_replay_id)
      if (replayAlreadyActive) {
        return
      }

      mixpanel.start_session_recording()
      ownsRecordingRef.current = true
    } catch (error) {
      logError(error, { context: 'startMixpanelBulkUploadRecording' })
      ownsRecordingRef.current = false
    }

    return (): void => {
      if (!ownsRecordingRef.current) {
        return
      }
      ownsRecordingRef.current = false

      try {
        mixpanel.stop_session_recording()
      } catch (error) {
        logError(error, { context: 'stopMixpanelBulkUploadRecording' })
      }
    }
  }, [isMixpanelReplaysEnabled, isProduction])
}
